import {Ability, WeaponProperty} from '../../../../generated/types';
import type {Character} from './character';
import {DiceFormula, parseDice} from './dice';
import {EquippedItem, EquipmentItem, hasProperty, isRangedWeapon} from './equipment';
import {weaponMasteryLabel} from './labels';

export type AttackDamage = {
	formula: DiceFormula;
	type?: string;
};

export type Attack = {
	key: string;
	title: string;
	attackBonus: number;
	damage: AttackDamage;
	/** урон при хвате двумя руками */
	versatileDamage?: AttackDamage;
	/** дальность в футах: «80/320» для дальнобойного, досягаемость для рукопашного */
	range: string;
	/** свойство мастерства, если персонаж им владеет */
	mastery?: string;
	properties: string[];
	/** предмет с описанием: строку атаки можно раскрыть */
	itemId?: string;
};

const MELEE_REACH = 5;
const LONG_REACH = 10;
const UNARMED_DAMAGE = 1;

export function weaponAttack(character: Character, {entry, item, weaponBonus}: EquippedItem, offHand = false): Attack {
	const modifier = weaponModifier(character, item);
	const magicAttack = weaponBonus.attackBonus;
	const damageBonus = (offHand ? Math.min(modifier, 0) : modifier) + weaponBonus.damageBonus;
	const damage = (dice: string | undefined) => ({
		formula: parseDice(dice || '', damageBonus),
		type: item.damageType,
	});
	const masteryId = character.weaponMasteries.includes(item.baseWeaponId) ? item.weaponMastery[0] : undefined;

	return {
		key: `${entry.key}${offHand ? ':offHand' : ''}`,
		title: item.baseTitle ? `${item.title} (${item.baseTitle})` : item.title,
		attackBonus: modifier + (isWeaponProficient(character, item) ? character.proficiencyBonus : 0) + magicAttack,
		damage: damage(item.damage),
		versatileDamage: item.versatileDamage && !offHand ? damage(item.versatileDamage) : undefined,
		range: weaponRange(item),
		mastery: masteryId && weaponMasteryLabel(masteryId),
		properties: item.weaponProperties,
		itemId: item.hasDescription ? item.id : undefined,
	};
}

export function unarmedStrike(character: Character): Attack {
	const strength = character.abilityModifier(Ability.Strength);
	const modifier = character.unarmedDexterity
		? Math.max(strength, character.abilityModifier(Ability.Dexterity))
		: strength;
	const die = character.unarmedDamageDie;
	const bonus = character.unarmedStrikeBonus;
	const damageModifier = modifier + bonus.damageBonus;

	return {
		key: 'unarmed-strike',
		title: 'Безоружный удар',
		attackBonus: modifier + character.proficiencyBonus + bonus.attackBonus,
		damage: {
			formula: die
				? parseDice(die, damageModifier)
				: {dice: [], bonus: Math.max(0, UNARMED_DAMAGE + damageModifier)},
			type: 'bludgeoning',
		},
		range: String(MELEE_REACH),
		properties: [],
	};
}

function weaponModifier(character: Character, item: EquipmentItem): number {
	const strength = character.abilityModifier(Ability.Strength);
	const dexterity = character.abilityModifier(Ability.Dexterity);
	if (hasProperty(item, WeaponProperty.Finesse)) return Math.max(strength, dexterity);
	return isRangedWeapon(item) ? dexterity : strength;
}

function isWeaponProficient(character: Character, item: EquipmentItem): boolean {
	const proficiencies = character.weaponProficiencies;
	if (proficiencies.includes(item.baseWeaponId) || proficiencies.includes(item.id)) return true;
	if (item.weaponCategory && proficiencies.includes(item.weaponCategory)) return true;
	if (item.weaponCategory !== 'Martial') return false;
	return (
		(proficiencies.includes('LightMartial') && hasProperty(item, WeaponProperty.Light)) ||
		(proficiencies.includes('FencingMartial') && hasProperty(item, WeaponProperty.Finesse))
	);
}

function weaponRange(item: EquipmentItem): string {
	if (isRangedWeapon(item) && item.range) return item.range;
	const reach = String(hasProperty(item, WeaponProperty.Reach) ? LONG_REACH : MELEE_REACH);
	return hasProperty(item, WeaponProperty.Thrown) && item.range ? `${reach}, ${item.range}` : reach;
}
